import React, {useCallback, useEffect, useState} from "react";
import {DashboardHero} from './dashboardui';
import './operationsui.css';
import {Alert, Button, Col, Form, FormControl, Modal, Row, Table} from "react-bootstrap";
import Pagination from "react-bootstrap/Pagination";
import {useUser} from "../UserContext";
import {useOrders} from "../OrderContext";
import {useCustomers} from "../CustomerContext";
import {
    clearAllOrderReturnFlags,
    clearOrderNegativeProfitFlag,
    clearOrderReturnFlag,
    fetchReturnedOrderAlerts,
    fetchUsers
} from "../api/api";
import {createOrderReturn} from "../api/api";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faUndo} from "@fortawesome/free-solid-svg-icons";
import {useTransactions} from "../contextjs/TransactionContext";
import {AccountingScopeSelect, useAccountingScope} from "./Accounting/accountingscope";

// profit logic for each order, defined outside the order function so that it can be exported and used in reports.js
export const calculatedOrderProfit = (order) => {
    return order.items.reduce((profit, item) => {
        const unitPrice = parseFloat(item.unit_price) || 0.00;
        const quantity = parseFloat(item.quantity) || 0;
        const returnedQuantity = parseFloat(item.returned_quantity) || 0;
        const netQuantity = quantity - returnedQuantity;
        const costPrice = parseFloat(item.cost_price) || 0.00; // from cost_price in order-item model
        const lineDiscount = parseFloat(item.discount) || 0.00;
        const netDiscount = quantity > 0 ? lineDiscount * (netQuantity / quantity) : 0; // allocate discount proportionally to net quantity
        // const conversionFactor = parseFloat(item.conversion_factor) || 1;

        // calculate profit for the net quantity sold
        const itemProfit = ((unitPrice - costPrice) * netQuantity) - netDiscount;

        return profit + itemProfit;
    }, 0).toFixed(2);
};

const nairobiDateKey = (value = new Date()) => {
    const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: 'Africa/Nairobi',
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    }).formatToParts(new Date(value));
    const values = Object.fromEntries(
        parts.filter((part) => part.type !== 'literal')
            .map((part) => [part.type, part.value])
    );
    return `${values.year}-${values.month}-${values.day}`;
};

const Orders = () => {
    const { user } = useUser();
    const scope = useAccountingScope();
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const [updatedOrder, setUpdatedOrder] = useState({
        customer: '',
        payment_method: '',
    });
    const [users, setUsers] = useState([]);

    const [showReturnModal, setShowReturnModal] = useState(false);
    const [returnQuantities, setReturnQuantities] = useState([]);

    // orders filter states
    const [customerFilter, setCustomerFilter] = useState('');
    const [startDateFilter, setStartDateFilter] = useState('');
    const [endDateFilter, setEndDateFilter] = useState('');
    const [paymentMethodFilter, setPaymentMethodFilter] = useState('');
    const [orderNumberFilter, setOrderNumberFilter] = useState('');
    const [productFilter, setProductFilter] = useState('');
    const [servedByFilter, setServedByFilter] = useState('');
    const [returnedOrderAlerts, setReturnedOrderAlerts] = useState([]);

    const { orders, totalOrders, fetchOrders, updateOrder, getOrderById } = useOrders();
    const { customers, fetchCustomers } = useCustomers();
    const { loadTransactions } = useTransactions(); // added to refresh transactions after return

    const [currentPage, setCurrentPage] = useState(1);
    const entriesPerPage = 20;

    useEffect(() => {
        if (scope.loadingScope || !scope.storeId) return;
        fetchOrders(currentPage, {
            searchTerm: customerFilter,
            start_date: startDateFilter,
            end_date: endDateFilter,
            payment_method: paymentMethodFilter,
            orderNumberFilter: orderNumberFilter, // because the fetchOrder helper from api.js looks for orderNumberFilter
            // but not the key order_number like the way searchTerm has been done.
            product_search: productFilter,
            served_by: servedByFilter,
            store_id: scope.storeId,
        })
        fetchCustomers(); // Fetch customers for the dropdown
    }, [fetchOrders, fetchCustomers, currentPage, customerFilter, startDateFilter, endDateFilter,
        paymentMethodFilter, orderNumberFilter, productFilter, servedByFilter, scope.loadingScope, scope.storeId]);

    useEffect(() => {
        setCurrentPage(1);
    }, [scope.storeId]);

    const loadReturnedOrderAlerts = useCallback(async () => {
        if (scope.loadingScope || !scope.storeId) return;
        if (!(user?.is_superuser || user?.groups?.includes?.('Admin') || user?.groups?.includes?.('Administrator'))) {
            setReturnedOrderAlerts([]);
            return;
        }

        try {
            const alerts = await fetchReturnedOrderAlerts({ store_id: scope.storeId });
            setReturnedOrderAlerts(alerts);
        } catch (error) {
            console.error('Error loading returned order alerts:', error);
            setReturnedOrderAlerts([]);
        }
    }, [scope.loadingScope, scope.storeId, user]);

    useEffect(() => {
        loadReturnedOrderAlerts();
    }, [loadReturnedOrderAlerts]);

    // added
    useEffect(() => {
        const loadUsers = async () => {
            try {
                const data = await fetchUsers();
                setUsers(data);
            } catch (error) {
                console.error('Error fetching users:', error);
            }
        };
        loadUsers();
    }, []);
    // end of added

    const handleShowModal = (order) => {
        setSelectedOrder(order);
        setShowModal(true);
        setIsEditMode(false);
    }

    const handleCloseModal = () => {
        setShowModal(false);
        setIsEditMode(false);
    };

    const enterEditMode = () => {
        setUpdatedOrder({
            customer: selectedOrder.customer_details ? selectedOrder.customer_details.id : '',
            payment_method: selectedOrder.payment_method,
        });
        setIsEditMode(true);
    };

    const handleUpdateOrder = async (e) => {
        e.preventDefault();
        try {
            const previousCustomer = selectedOrder.customer_details ? selectedOrder.customer_details.customer_name : 'Walk-in';
            const previousPaymentMethod = selectedOrder.payment_method;

            const updatedOrderData = {
                ...selectedOrder,
                customer: updatedOrder.customer,
                payment_method: updatedOrder.payment_method,
                items: selectedOrder.items.map(item => ({
                    product_id: item.product_details.id,
                    quantity: parseFloat(item.quantity.toFixed(2)),
                    returned_quantity: parseFloat((item.returned_quantity || 0).toFixed(2)),
                    unit_price: parseFloat(item.unit_price.toFixed(2)),
                    unit_id: item.unit_id || null,
                }))
            };

            //determine what has changed
            let details = [];
            if (updatedOrderData.customer !== selectedOrder.customer) {
                const newCustomer = updatedOrder.customer_details ? updatedOrder.customer_details.customer_name : 'Walk-in';
                details.push(`Customer - ${previousCustomer} to ${newCustomer}`);
            }
            if (updatedOrderData.payment_method !== selectedOrder.payment_method) {
                details.push(`Payment Method - ${previousPaymentMethod} to ${updatedOrderData.payment_method}`);
            }

            // if nothing has changed, do not add to audit log
            if (details.length === 0) {
                alert("No changes made.") ;
                return;
            }

            console.log('Updated order data:', updatedOrderData);
            await updateOrder(updatedOrderData);

            const newAuditEntry = {
                action: `Update by ${user.username}`, // update to include username
                date: new Date().toLocaleString(),
                details: `Updated order details: ${details.join(', ')}`
            };

            const updatedOrderDataWithLog = {
                ...updatedOrderData,
                audit_log: [...selectedOrder.audit_log, newAuditEntry]
            };

            await updateOrder(updatedOrderDataWithLog);

            setSelectedOrder(updatedOrderDataWithLog);
            handleCloseModal();
            fetchOrders(currentPage, {
                searchTerm: customerFilter,
                start_date: startDateFilter,
                end_date: endDateFilter,
                payment_method: paymentMethodFilter,
                orderNumberFilter,
                product_search: productFilter,
                served_by: servedByFilter,
                store_id: scope.storeId,
            });
        } catch (error) {
            console.error('Error updating order:', error);
        }
    };

    // function to open return modal and initialize return quantities
    const handleShowReturnModal = (order) => {
        setSelectedOrder(order);
        setShowReturnModal(true);
        setReturnQuantities(
            order.items.map(item => ({
                ...item,
                return_quantity: 0, //changed from item.quantity to 0, initialize with 0 to avoid prefilled values
                amount: parseFloat(item.unit_price) * item.quantity,
                max_returnable: item.quantity - (item.returned_quantity || 0), // calculate max returnable quantity
                unit_name: item.unit_name || '', // include unit name for display
                unit_id: item.unit_id || null,
                conversion_factor: item.conversion_factor || 1.0,
                unit_price: parseFloat(item.unit_price),
            }))
        );
    };
    // console.log('Return quantities:', returnQuantities);

    const handleCloseReturnModal = () => setShowReturnModal(false);

    const handleReturnQuantityChange = (e, index) => {
        const inputValue = parseFloat(e.target.value) || 0;
        const newReturnQuantity = Math.min(inputValue, returnQuantities[index].max_returnable); // ensure it doesn't exceed remaining quantity

        // limit to 2 decimal places
        const roundedReturnQuantity = parseFloat(newReturnQuantity.toFixed(2));

        setReturnQuantities(prevQuantities => {
            const updatedItems = [...prevQuantities];
            const unitPrice = parseFloat(updatedItems[index].unit_price) || 0.00;

            updatedItems[index] = {
                ...updatedItems[index],
                return_quantity: roundedReturnQuantity,
                amount: parseFloat((unitPrice * roundedReturnQuantity).toFixed(2)), // calculate amount and round to 2 decimal places
            };
            return updatedItems;
        });
    };

    // processReturn is wrapped using useCallback to ensure it's memoized and avoid unnecessary re-renders
    const processReturn = useCallback(async (quantities)=> {
        // console.log('Return quantities before filtering:', returnQuantities);

        // use the quantities passed as argument if available, otherwise use the state
        const currentReturnQuantities = quantities || returnQuantities;

        const returnedItems = currentReturnQuantities
            .filter(item => item.return_quantity > 0 && item.return_quantity <= item.max_returnable)
            .map(item => {
                const unitPrice = parseFloat(item.unit_price) || 0.00;
                const quantity = parseFloat(item.return_quantity) || 0.00;
                const conversionFactor = parseFloat(item.conversion_factor) || 1.0;

                return {
                    product_id: item.product_details.id,
                    product_details: item.product_details,
                    quantity: parseFloat(quantity.toFixed(2)),
                    unit_price: parseFloat(unitPrice.toFixed(2)),
                    unit_id: item.unit_id || null,
                    unit_name: item.unit_name || '',
                    unit_abbreviation: item.unit_abbreviation || '',
                    conversion_factor: parseFloat(conversionFactor.toFixed(6)),
                }
            });

        if (returnedItems.length === 0) {
            alert("No valid items to return.")
            return;
        }

        // add entry to audit log
        const newAuditEntry = {
            action: `Return by ${user.username}`, // update to include username
            date: new Date().toISOString(),
            details: `Returned items: ${returnedItems.map(item => `${item.quantity} ${item.product_details.product_name} (${item.unit_name || item.unit_abbreviation || 'Unit'})`).join(', ')}`
        };

        // calculate discount percentage
        const discountPercentage = selectedOrder.discount / selectedOrder.total_amount || 0;

        // calculate total return amount adjusted for discount
        const totalReturnAmount = returnedItems.reduce((total, item) => {
            const discountedUnitPrice = item.unit_price * (1 - discountPercentage);
            return total + (item.quantity * discountedUnitPrice);
        }, 0);

        // safely update the final_amount
        // const updatedFinalAmount = Math.max(selectedOrder.final_amount - totalReturnAmount, 0);

        const returnData = {
            order: selectedOrder.id,
            returned_items: returnedItems,
            // return_date: new Date().toISOString().split('T')[0],
            // audit_log: [...selectedOrder.audit_log || [], newAuditEntry]
        };
        // console.log('Return data in order.js:', returnData);

        try {
            await createOrderReturn(returnData);

            // fetch a fresh version of a specific order
            const refreshedOrder = await getOrderById(selectedOrder.id);
            setSelectedOrder(refreshedOrder);

            handleCloseReturnModal();
            fetchOrders(currentPage, {
                searchTerm: customerFilter,
                start_date: startDateFilter,
                end_date: endDateFilter,
                payment_method: paymentMethodFilter,
                orderNumberFilter,
                product_search: productFilter,
                served_by: servedByFilter,
                store_id: scope.storeId,
            }); // refresh orders after return
            loadReturnedOrderAlerts();
            loadTransactions(); // refresh transactions after return
        } catch(error) {
            console.error('Error processing return:', error);
            alert(error.response?.data?.detail || 'Could not process the order return.');
        }
    }, [returnQuantities, selectedOrder, user.username, fetchOrders, currentPage, customerFilter, startDateFilter,
        endDateFilter, paymentMethodFilter, orderNumberFilter, productFilter, servedByFilter, scope.storeId, getOrderById,
        loadReturnedOrderAlerts, loadTransactions]);

    // function to handle returning of all items
    const handleReturnAll = () => {
        const updatedQuantities = returnQuantities.map(item => ({
            ...item,
            return_quantity: item.max_returnable,
            amount: (parseFloat(item.unit_price) * item.max_returnable).toFixed(2),
        }));
        // update the state
        setReturnQuantities(updatedQuantities);

        // call the processReturn with the updated quantities
        processReturn(updatedQuantities);
        // passing updatedQuantities directly to processReturn to ensure it uses the updated data
    };


    const formatDate = (dateString) => {
        const date = new Date(dateString);
        const d = String(date.getDate()).padStart(2, '0');
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const y = date.getFullYear();
        const h = String(date.getHours()).padStart(2, '0');
        const min = String(date.getMinutes()).padStart(2, '0');

        return `${d}-${m}-${y} ${h}:${min}`;
    }

    const totalPages = Math.ceil(totalOrders / entriesPerPage);
    const isAdmin = user?.is_superuser || user?.groups?.includes?.('Admin') || user?.groups?.includes?.('Administrator');
    const canReturnSelectedOrder = Boolean(
        selectedOrder && (
                isAdmin ||
                nairobiDateKey(selectedOrder.created_at) === nairobiDateKey()
        )
    );
    const negativeProfitOrders = orders.filter((order) => Number(calculatedOrderProfit(order)) < 0 && !order.negative_profit_flag_cleared);
    const returnedOrdersNeedingReview = returnedOrderAlerts;

    const handleClearNegativeProfitFlag = async (orderId) => {
        try {
            await clearOrderNegativeProfitFlag(orderId);
            fetchOrders(currentPage, {
                searchTerm: customerFilter,
                start_date: startDateFilter,
                end_date: endDateFilter,
                payment_method: paymentMethodFilter,
                orderNumberFilter,
                product_search: productFilter,
                served_by: servedByFilter,
                store_id: scope.storeId,
            });
        } catch (error) {
            console.error('Error clearing negative profit flag:', error);
            alert('Could not clear the negative profit flag.');
        }
    };

    const handleClearReturnFlag = async (orderId) => {
        try {
            await clearOrderReturnFlag(orderId);
            fetchOrders(currentPage, {
                searchTerm: customerFilter,
                start_date: startDateFilter,
                end_date: endDateFilter,
                payment_method: paymentMethodFilter,
                orderNumberFilter,
                product_search: productFilter,
                served_by: servedByFilter,
                store_id: scope.storeId,
            });
            loadReturnedOrderAlerts();
        } catch (error) {
            console.error('Error clearing order return flag:', error);
            alert('Could not clear the order return flag.');
        }
    };

    const handleClearAllReturnFlags = async () => {
        if (!window.confirm(`Clear all ${returnedOrdersNeedingReview.length} returned order alerts for ${scope.scopeLabel}?`)) {
            return;
        }

        try {
            await clearAllOrderReturnFlags({ store_id: scope.storeId });
            fetchOrders(currentPage, {
                searchTerm: customerFilter,
                start_date: startDateFilter,
                end_date: endDateFilter,
                payment_method: paymentMethodFilter,
                orderNumberFilter,
                product_search: productFilter,
                served_by: servedByFilter,
                store_id: scope.storeId,
            });
            loadReturnedOrderAlerts();
        } catch (error) {
            console.error('Error clearing returned order alerts:', error);
            alert('Could not clear returned order alerts.');
        }
    };

    // Calculate total profit for the selected order
    const totalProfit = selectedOrder ? parseFloat(calculatedOrderProfit(selectedOrder)).toFixed(2) : '0.00';

    // Define VAT rate
    const VAT_RATE = 0.16;

    // Function to calculate VAT amount
    const calculateVatAmount = () => {
        return selectedOrder.items.reduce((total, item) => {
            const itemTotal = item.unit_price * item.quantity;
            if (item.product_details.is_vat_applicable) {
              const preTaxPrice = itemTotal / (1 + VAT_RATE);
              const vatAmount = itemTotal - preTaxPrice;
              return total + vatAmount;
            } else {
              return total;
            }
        }, 0);
    };

    // added for reprint
    const calculateVatAmountFromOrder = (order) => {
        return order.items.reduce((total, item) => {
            const grossTotal = parseFloat(item.unit_price) * parseFloat(item.quantity);
            const itemTotal = Math.max(0, grossTotal - (parseFloat(item.discount) || 0));
            if (item.product_details.is_vat_applicable) {
                const preTaxPrice = itemTotal / (1 + 0.16);
                const vatAmount = itemTotal - preTaxPrice;
                return total + vatAmount;
            }
            return total;
        }, 0);
    };
    // helper function for pre-tax subtotal
    const calculatePreTaxSubtotalFromOrder = (order) => {
        return order.items.reduce((total, item) => {
            const itemPrice = parseFloat(item.unit_price) || 0.00;
            const itemQty = parseFloat(item.quantity) || 0.00;
            const itemTotal = Math.max(0, (itemPrice * itemQty) - (parseFloat(item.discount) || 0));

            if (item.product_details.is_vat_applicable) {
                const preTax = (itemTotal / (1 + VAT_RATE));
                return total + preTax;
            } else {
                return total + itemTotal;
            }
        }, 0);
    };

    const handleReprintClick = async (orderId) => {
        try {
            // fetch single order with items
            const order = await getOrderById(orderId);
            setSelectedOrder(order);
            console.log("Fetched single order:", order);
            // call the reprint function
            await handleReprintReceipt(order);
        } catch (error) {
            console.error("Error fetching order for reprint:", error);
            alert("Error fetching order for reprint. Please try again.");
        }
    }

    const handleReprintReceipt = async(order) => {
        if (!order || !order.items) {
            console.error("Invalid order data:", order);
            alert("No items found in the order. Please try again.");
            return;
        }
        try {
            // build cart data similar to checkout.js
            const cartData = order.items.map(item => ({
                name: item.product_details.product_name,
                quantity: parseFloat(item.quantity) || 0,
                price: parseFloat(item.unit_price) || 0,
                unit_id: item.unit_id || null,
                unit_name: item.unit_name || '',
                quantity_discount: parseFloat(item.discount) || 0,
                quantity_discount_reason: item.discount_reason || '',
                discounted_subtotal: Math.max(0, (parseFloat(item.unit_price) || 0) * (parseFloat(item.quantity) || 0) - (parseFloat(item.discount) || 0)),
            }));

            // use existing discount, final_amount
            // instead of calculateVatAmount(), which uses selectedOrder, use calculateVatAmountFromOrder(order)
            const vatAmount = calculateVatAmountFromOrder(order);
            const preTaxSubtotal = calculatePreTaxSubtotalFromOrder(order);
            const total = Math.ceil(order.final_amount);
            const discountVal = parseFloat(order.discount) || 0;

            let isoCreatedAt;
            try {
                isoCreatedAt = new Date(order.created_at).toISOString();
            } catch (error) {
                console.error("Error parsing order created_at:", error);
                isoCreatedAt = new Date().toISOString();
            }

            // prepare the body to send to /print-receipt/
            const receiptData = {
                cart: cartData,
                served_by: order.served_by_username,
                // created_at: order.created_at,
                created_at: isoCreatedAt,
                order_number: order.order_number || "",
                total: total,
                discount: discountVal,
                vatAmount: vatAmount,
                preTaxSubtotal: preTaxSubtotal,
                payment_method: order.payment_method,
                amountPaid: total,
                change: 0,
                customer: order.customer_details ? order.customer_details.customer_name : 'Walk-in',
            };

            // the API call
            const printResponse = await fetch("/api/print-receipt/", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: JSON.stringify(receiptData),
            });
            if (!printResponse.ok) {
                console.warn("Receipt print request failed:", printResponse.status);
            }
            alert("Receipt reprint request sent successfully.");
        } catch (error) {
            console.error("Error reprinting receipt:", error);
            alert("Error reprinting receipt. Please try again.");
        }
    };

    const maxLinks = 5;
    let startLink = Math.max(currentPage - 2, 1);
    let endLink = Math.min(startLink + maxLinks - 1, totalPages);

    if (endLink > totalPages) {
      endLink = totalPages;
      startLink = Math.max(endLink - (maxLinks - 1), 1);
    }

    const pageNumbersToShow = [];
    for (let i = startLink; i <= endLink; i++) {
      pageNumbersToShow.push(i);
    }


    return (
        <div className="wh-dashboard ops-page">
            <DashboardHero eyebrow="Sales operations" title="Orders" description="Find sales, review returns and inspect order details from one workspace." action="Sales reports" to="/reports" />
            {isAdmin && negativeProfitOrders.length > 0 && (
                <Alert variant="danger" className="mb-3">
                    <div className="fw-bold mb-2">Orders with negative profit need inspection</div>
                    {negativeProfitOrders.map((order) => (
                        <div key={order.id} className="d-flex justify-content-between align-items-center gap-2 mb-2">
                            <span>
                                Order {order.order_number || order.id}: Profit Ksh. {calculatedOrderProfit(order)}
                            </span>
                            <Button size="sm" variant="outline-danger" onClick={() => handleClearNegativeProfitFlag(order.id)}>
                                Clear
                            </Button>
                        </div>
                    ))}
                </Alert>
            )}
            {isAdmin && returnedOrdersNeedingReview.length > 0 && (
                <Alert variant="warning" className="mb-3">
                    <div className="d-flex justify-content-between align-items-center gap-2 mb-2">
                        <div className="fw-bold">Returned orders need review</div>
                        <Button size="sm" variant="outline-warning" onClick={handleClearAllReturnFlags}>
                            Clear all
                        </Button>
                    </div>
                    {returnedOrdersNeedingReview.map((order) => (
                        <div key={order.id} className="d-flex justify-content-between align-items-center gap-2 mb-2">
                            <span>Order {order.order_number || order.id} has returned items.</span>
                            <Button size="sm" variant="outline-warning" onClick={() => handleClearReturnFlag(order.id)}>
                                Clear
                            </Button>
                        </div>
                    ))}
                </Alert>
            )}
            <div className="wh-section-heading"><h2>Order register</h2><span>{totalOrders} matching orders · Page {currentPage} of {Math.max(1, totalPages)}</span></div>
            <div className="filter-section mt-3 mb-3">
                <Row className="g-3 align-items-end">
                    {scope.isAdmin && (
                        <Col md={3}>
                            <AccountingScopeSelect scope={scope} label="Order Scope" />
                        </Col>
                    )}
                    <Col md={3}>
                        <FormControl
                            aria-label="Order number"
                            placeholder="order number"
                            onChange={(e) => setOrderNumberFilter(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <FormControl
                            aria-label="Customer name"
                            placeholder="Search by customer name"
                            onChange={(e) => setCustomerFilter(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <FormControl
                            aria-label="Product name"
                            value={productFilter}
                            placeholder="Search by product name"
                            onChange={(e) => {
                                setProductFilter(e.target.value);
                                setCurrentPage(1);
                            }}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>From date</Form.Label>
                        <FormControl
                            type="date"
                            aria-label="Start date"
                            placeholder="Start Date"
                            onChange={(e) => setStartDateFilter(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Label>Through date</Form.Label>
                        <FormControl
                            type="date"
                            aria-label="End date"
                            placeholder="End Date"
                            onChange={(e) => setEndDateFilter(e.target.value)}
                        />
                    </Col>
                    <Col md={3}>
                        <Form.Control
                            as="select"
                            aria-label="Payment method"
                            value={paymentMethodFilter}
                            onChange={(e) => setPaymentMethodFilter(e.target.value)}
                        >
                            <option value="">All Payment Methods</option>
                            <option value="cash">Cash</option>
                            <option value="mpesa">Mpesa</option>
                            <option value="cashAndMpesa">Cash & Mpesa</option>
                            <option value="customerAccount">Customer Account</option>
                        </Form.Control>
                    </Col>
                    <Col md={3}>
                        <Form.Select
                            aria-label="Order-entry user"
                            value={servedByFilter}
                            onChange={(e) => setServedByFilter(e.target.value)}
                        >
                            <option value="">All Served By</option>
                            {users.map(u => (
                                <option key={u.id} value={u.id}>
                                    {u.username}
                                </option>
                            ))}
                        </Form.Select>
                    </Col>
                </Row>
            </div>
            <Table responsive hover>
                <thead>
                    <tr>
                        <th>Order Number</th>
                        <th>Customer Name</th>
                        <th>Order Date</th>
                        <th>Final Amount</th>
                        <th>Profit</th>
                        <th>Payment Method</th>
                        <th>Served By</th>
                        <th>Returns</th>
                        <th>Actions</th>
                    </tr>
                </thead>
                <tbody>
                    {orders.map((order) => (
                        <tr key={order.id}>
                            <td>{order.order_number || order.id}</td>
                            <td>{order.customer_details ? order.customer_details.customer_name : 'Walk-in'}</td>
                            <td>{formatDate(order.created_at)}</td>
                            <td>{order.final_amount}</td>
                            <td>{calculatedOrderProfit(order)}</td>
                            <td>{order.payment_method}</td>
                            <td>{order.served_by_username || '-'}</td>
                            <td>{order.has_return && !order.return_flag_cleared ? <span className="return-flag">Returned</span> : '-'}</td>
                            <td>
                                <Button variant="info" onClick={() => handleShowModal(order)}
                                className="button-spacing">View Details</Button>
                                <Button variant="warning" onClick={() => handleReprintClick(order.id)}
                                        className="button-spacing">Reprint</Button>
                                {/*<Button variant="danger" onClick={() => handleDeleteOrder(order.id)}>Delete</Button>*/}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </Table>

            <div className="mt-4 d-flex justify-content-center">
                <Pagination>
                    <Pagination.First onClick={() => setCurrentPage(1)} disabled={currentPage === 1} />
                    <Pagination.Prev
                        onClick={() => setCurrentPage((prev) => Math.max(prev - 1, 1))}
                        disabled={currentPage === 1}
                    />
                    {/*{Array.from({ length: totalPages }, (_, i) => i + 1).map((pageNum) => (*/}
                    {pageNumbersToShow.map((pageNum) => (
                        <Pagination.Item
                            key={pageNum}
                            active={pageNum === currentPage}
                            onClick={() => setCurrentPage(pageNum)}
                        >
                            {pageNum}
                        </Pagination.Item>
                    ))}
                    <Pagination.Next
                        // onClick={() => setCurrentPage((prev) => Math.min(prev + 1, totalPages))}
                        onClick={() => setCurrentPage(currentPage + 1)}
                        disabled={currentPage === totalPages}/>
                    <Pagination.Last
                        onClick={() => setCurrentPage(totalPages)}
                        disabled={currentPage === totalPages}
                        // pagination
                    />
                </Pagination>
            </div>

            {showModal && selectedOrder && (
                <Modal show={showModal} onHide={handleCloseModal} size="xl">
                    <Modal.Header closeButton>
                        <Modal.Title>Order Details</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        {!isEditMode ? (
                            <>
                                <h5>Order Summary</h5>
                                {/*<p><strong>Order ID: </strong>{selectedOrder.id}</p>*/}
                                <p><strong>Order Number: </strong>{selectedOrder.order_number || selectedOrder.id}</p>
                                <p><strong>Order Date & Time: </strong>{formatDate(selectedOrder.created_at)}</p>
                                <p><strong>Customer: </strong>{selectedOrder.customer_details ?
                                    selectedOrder.customer_details.customer_name : 'Walk-in'}</p>
                                <p><strong>Payment Method: </strong>{selectedOrder.payment_method}</p>
                                <p><strong>Total Amount: </strong>{selectedOrder.total_amount}</p>
                                <p><strong>VAT: </strong>{calculateVatAmount().toFixed(2)}</p>
                                <p><strong>Discount: </strong>{selectedOrder.discount}</p>
                                <p><strong>Total Profit: </strong>{parseFloat(totalProfit).toFixed(2)}</p>
                                <p><strong>Final Amount: </strong>{selectedOrder.final_amount}</p>
                                <p><strong>Notes: </strong>{selectedOrder.order_notes || '-'}</p>

                                <Button variant="primary" onClick={enterEditMode}
                                        className="button-spacing">Edit</Button>
                                {canReturnSelectedOrder && (
                                    <Button variant="warning" onClick={() => handleShowReturnModal(selectedOrder)}
                                            className="button-spacing">Return Order</Button>
                                )}
                            </>
                        ) : (
                            <Form onSubmit={handleUpdateOrder}>
                                <Form.Group>
                                    <Form.Label>Customer:</Form.Label>
                                    <Form.Control as="select" value={updatedOrder.customer} onChange={(e) => setUpdatedOrder({ ...updatedOrder, customer: e.target.value })}>
                                        <option value="">Walk-in</option>
                                        {customers.map((customer) => (
                                            <option key={customer.id} value={customer.id}>{customer.customer_name}</option>
                                        ))}
                                    </Form.Control>
                                </Form.Group>
                                <Form.Group controlId="formPaymentMethod">
                                    <Form.Label>Payment Method:</Form.Label>
                                    <Form.Control as="select" value={updatedOrder.payment_method} onChange={(e) => setUpdatedOrder({ ...updatedOrder, payment_method: e.target.value })}>
                                        <option value="cash">Cash</option>
                                        <option value="mpesa">Mpesa</option>
                                        <option value="cashAndmpesa">Cash & Mpesa</option>
                                        <option value="customerAccount">Customer Account</option>
                                    </Form.Control>
                                </Form.Group>
                                <br/>
                                <Button variant="primary" type="submit" className="button-spacing">Save Changes</Button>
                                <Button variant="secondary" onClick={() => setIsEditMode(false)}>Cancel</Button>
                            </Form>
                        )}
                        <div className="order-items mt-3">
                            <h5>Order Items</h5>
                            <Table striped bordered hover>
                                <thead>
                                    <tr>
                                        <th>Product</th>
                                        <th>Cost Price</th>
                                        <th>Unit Price Ksh.</th>
                                        <th>Quantity</th>
                                        <th>Subtotal</th>
                                        <th>Profit per item</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {selectedOrder.items && selectedOrder.items.map((item, index) => {
                                        const unitPrice = parseFloat(item.unit_price) || 0.00;
                                        const quantity = parseFloat(item.quantity) || 0;
                                        const returnedQuantity = parseFloat(item.returned_quantity) || 0;
                                        const netQuantity = quantity - returnedQuantity;
                                        const costPrice = parseFloat(item.cost_price) || 0.00; // from cost_price in orderitem model
                                        const subtotal = unitPrice * netQuantity;
                                        const itemProfit = (unitPrice - costPrice) * netQuantity;

                                        return (
                                            <tr key={index}>
                                                <td>
                                                    {item.product_details.product_name}
                                                    {item.unit_name && item.unit_name !== 'Base Unit' ? ` ${item.unit_name}` : ''}
                                                    {/*{item.unit_id ? ` ${item.unit_name || item.unit_abbreviation || ''}` : ''}*/}
                                                </td>
                                                <td>{costPrice.toFixed(2)}</td>
                                                <td>{unitPrice.toFixed(2)}</td>
                                                <td>
                                                    {/*{parseFloat(item.quantity).toFixed(2)}*/}
                                                    {quantity.toFixed(2)}
                                                    {/*{item.returned_quantity > 0 && (*/}
                                                    {returnedQuantity > 0 && (
                                                        <>
                                                            <br/>
                                                            <span style={{color: 'red'}}>
                                                                <FontAwesomeIcon
                                                                    icon={faUndo}/>{returnedQuantity.toFixed(2)}
                                                            </span>
                                                        </>
                                                    )}
                                                </td>
                                                <td>
                                                    {/*{(item.unit_price * item.quantity).toFixed(2)}*/}
                                                    {subtotal.toFixed(2)}
                                                    {/*{item.returned_quantity > 0 && (*/}
                                                    {returnedQuantity > 0 && (
                                                        <>
                                                            <br/>
                                                            <span style={{color: 'red'}}>
                                                                {/*-{(item.unit_price * item.returned_quantity).toFixed(2)}*/}
                                                                -{(unitPrice * returnedQuantity).toFixed(2)}
                                                            </span>
                                                        </>
                                                    )}
                                                </td>
                                                <td>{itemProfit.toFixed(2)}</td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        </div>
                        {selectedOrder.audit_log && selectedOrder.audit_log.length > 0 && (
                            <div className="audit-log mt-3">
                                <h5><strong>Audit Log</strong></h5>
                                <Table striped bordered hover>
                                    <thead>
                                        <tr>
                                            <th>Action</th>
                                            <th>Date & Time</th>
                                            <th>Details</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {selectedOrder.audit_log.map((entry, index) => (
                                            <tr key={index}>
                                                <td>{entry.action}</td>
                                                <td>
                                                    {new Date(entry.date).toLocaleString('en-GB', {
                                                        day: '2-digit',
                                                        month: '2-digit',
                                                        year: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit',
                                                        second: '2-digit'
                                                    })}
                                                </td>
                                                <td>{entry.details}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </Table>
                            </div>
                        )}
                    </Modal.Body>
                </Modal>
            )}

            {showReturnModal && (
                <Modal show={showReturnModal} onHide={handleCloseReturnModal} size="lg">
                    <Modal.Header closeButton>
                        <Modal.Title>Return Order</Modal.Title>
                    </Modal.Header>
                    <Modal.Body>
                        <div className="order-items mt-3">
                            <h5>Return Items</h5>
                            {/*<Button variant="danger" className="mb-3" onClick={handleReturnAll}>Return All</Button>*/}
                            <Table striped bordered hover>
                                <thead>
                                <tr>
                                    <th>Product</th>
                                    <th>Quantity</th>
                                    <th>Return Quantity</th>
                                    <th>Unit Price Ksh.</th>
                                    <th>Amount</th>
                                </tr>
                                </thead>
                                <tbody>
                                    {returnQuantities.map((item, index) => (
                                        <tr key={index}>
                                            <td>
                                                {item.product_details.product_name}
                                                {item.unit_name ? ` (${item.unit_name})` : ''}
                                            </td>
                                            <td>{parseFloat(item.quantity).toFixed(2)}</td>
                                            <td>
                                                <Form.Control
                                                    type="number"
                                                    // value={item.returnQuantity}
                                                    value={item.return_quantity}
                                                    min="0"
                                                    max={item.max_returnable}
                                                    step="any"
                                                    onChange={(e) => handleReturnQuantityChange(e, index)}
                                                />
                                            </td>
                                            <td>{parseFloat(item.unit_price).toFixed(2)}</td>
                                            <td>{parseFloat(item.amount).toFixed(2)}</td>
                                        </tr>
                                    ))}
                                </tbody>
                            </Table>
                        </div>
                        {/*<Button variant="primary" onClick={processReturn} className="button-spacing">Process Return</Button>*/}
                        <Button variant="primary" onClick={() => processReturn()} className="button-spacing">Process Return</Button>

                        <Button variant="danger" onClick={handleReturnAll} className="button-spacing">Return All</Button>

                        <Button variant="secondary" onClick={handleCloseReturnModal}>Cancel</Button>
                    </Modal.Body>
                </Modal>
            )}
        </div>
    );
};

export default Orders;
